// Comprehensive indicator library for the Pro Trade terminal.
// Each indicator is described by a config-driven definition (IND_DEFS) so the
// chart can create/destroy series and feed data generically — no giant switch.
//
// Inputs are arrays of candles: { time, open, high, low, close }.
// `calc(candles, { period, mult })` returns an object keyed by series-key, each
// value being a lightweight-charts-ready array of { time, value, color? }.
// Series whose value is null at a given index are filtered out.

// ── primitive math ────────────────────────────────────────────────────────
const lineData = (c, arr) =>
  c.map((x, i) => (arr[i] == null || Number.isNaN(arr[i]) ? null : { time: x.time, value: arr[i] })).filter(Boolean);

const emaArr = (vals, p) => {
  if (vals.length < p) return vals.map(() => null);
  const k = 2 / (p + 1);
  const out = [];
  let prev;
  vals.forEach((v, i) => {
    if (i < p - 1) { out.push(null); return; }
    if (i === p - 1) prev = vals.slice(0, p).reduce((a, b) => a + b, 0) / p;
    else prev = v * k + prev * (1 - k);
    out.push(prev);
  });
  return out;
};

const smaArr = (vals, p) =>
  vals.map((_, i) => (i < p - 1 ? null : vals.slice(i - p + 1, i + 1).reduce((a, b) => a + b, 0) / p));

const highest = (c, p) => c.map((_, i) => (i < p - 1 ? null : Math.max(...c.slice(i - p + 1, i + 1).map((x) => x.high))));
const lowest = (c, p) => c.map((_, i) => (i < p - 1 ? null : Math.min(...c.slice(i - p + 1, i + 1).map((x) => x.low))));

// Wilder-smoothed true range (ATR raw values, aligned to candles).
const atrArr = (c, p) => {
  const tr = c.map((x, i) => {
    if (i === 0) return x.high - x.low;
    const pc = c[i - 1].close;
    return Math.max(x.high - x.low, Math.abs(x.high - pc), Math.abs(x.low - pc));
  });
  const out = c.map(() => null);
  if (c.length <= p) return out;
  let prev = tr.slice(1, p + 1).reduce((a, b) => a + b, 0) / p;
  out[p] = prev;
  for (let i = p + 1; i < c.length; i++) {
    prev = (prev * (p - 1) + tr[i]) / p;
    out[i] = prev;
  }
  return out;
};

const rsiArr = (vals, p) => {
  const out = vals.map(() => null);
  if (vals.length <= p) return out;
  let gain = 0, loss = 0;
  for (let i = 1; i <= p; i++) { const d = vals[i] - vals[i - 1]; if (d >= 0) gain += d; else loss -= d; }
  gain /= p; loss /= p;
  out[p] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss);
  for (let i = p + 1; i < vals.length; i++) {
    const d = vals[i] - vals[i - 1];
    const g = d >= 0 ? d : 0, l = d < 0 ? -d : 0;
    gain = (gain * (p - 1) + g) / p;
    loss = (loss * (p - 1) + l) / p;
    out[i] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss);
  }
  return out;
};

const emaFull = (vals, p) => {
  const k = 2 / (p + 1);
  const out = [];
  let prev = vals[0];
  vals.forEach((v, i) => { prev = i === 0 ? v : v * k + prev * (1 - k); out.push(prev); });
  return out;
};

// ── per-indicator calculators ──────────────────────────────────────────────
const calcEma = (c, { period }) => ({ line: lineData(c, emaArr(c.map((x) => x.close), period)) });
const calcSma = (c, { period }) => ({ line: lineData(c, smaArr(c.map((x) => x.close), period)) });

const calcBoll = (c, { period, mult = 2 }) => {
  const cl = c.map((x) => x.close);
  const mid = smaArr(cl, period);
  const up = [], lo = [];
  cl.forEach((_, i) => {
    if (i < period - 1) { up.push(null); lo.push(null); return; }
    const s = cl.slice(i - period + 1, i + 1), m = mid[i];
    const sd = Math.sqrt(s.reduce((a, b) => a + (b - m) ** 2, 0) / period);
    up.push(m + mult * sd); lo.push(m - mult * sd);
  });
  return { upper: lineData(c, up), mid: lineData(c, mid), lower: lineData(c, lo) };
};

const calcVwap = (c) => {
  let cumPV = 0, cumV = 0;
  // Deriv forex/synthetic candles carry no true volume, so we weight by the
  // bar range (high-low) as a transparent proxy — a standard fallback.
  const out = c.map((x) => {
    const tp = (x.high + x.low + x.close) / 3;
    const v = Math.max(x.high - x.low, 1e-9);
    cumPV += tp * v; cumV += v;
    return cumPV / cumV;
  });
  return { line: lineData(c, out) };
};

const calcRsi = (c, { period }) => ({ line: lineData(c, rsiArr(c.map((x) => x.close), period)) });

const calcMacd = (c, { fast = 12, slow = 26, sigp = 9 } = {}) => {
  const cl = c.map((x) => x.close);
  const ef = emaFull(cl, fast), es = emaFull(cl, slow);
  const macd = cl.map((_, i) => ef[i] - es[i]);
  const signal = emaFull(macd, sigp);
  return {
    hist: c.map((x, i) => ({ time: x.time, value: macd[i] - signal[i], color: macd[i] - signal[i] >= 0 ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)" })),
    macd: c.map((x, i) => ({ time: x.time, value: macd[i] })),
    signal: c.map((x, i) => ({ time: x.time, value: signal[i] })),
  };
};

const calcAtr = (c, { period }) => ({ line: lineData(c, atrArr(c, period)) });

const calcAdx = (c, { period }) => {
  const n = c.length;
  const out = { adx: c.map(() => null), plusDi: c.map(() => null), minusDi: c.map(() => null) };
  if (n <= period * 2) return { adx: [], plusDi: [], minusDi: [] };
  const tr = [], plusDm = [], minusDm = [];
  for (let i = 0; i < n; i++) {
    if (i === 0) { tr.push(0); plusDm.push(0); minusDm.push(0); continue; }
    const up = c[i].high - c[i - 1].high;
    const down = c[i - 1].low - c[i].low;
    plusDm.push(up > down && up > 0 ? up : 0);
    minusDm.push(down > up && down > 0 ? down : 0);
    const pc = c[i - 1].close;
    tr.push(Math.max(c[i].high - c[i].low, Math.abs(c[i].high - pc), Math.abs(c[i].low - pc)));
  }
  let trS = tr.slice(1, period + 1).reduce((a, b) => a + b, 0);
  let pS = plusDm.slice(1, period + 1).reduce((a, b) => a + b, 0);
  let mS = minusDm.slice(1, period + 1).reduce((a, b) => a + b, 0);
  const dx = c.map(() => null);
  for (let i = period + 1; i < n; i++) {
    trS = trS - trS / period + tr[i];
    pS = pS - pS / period + plusDm[i];
    mS = mS - mS / period + minusDm[i];
    const pdi = trS === 0 ? 0 : (100 * pS) / trS;
    const mdi = trS === 0 ? 0 : (100 * mS) / trS;
    out.plusDi[i] = pdi; out.minusDi[i] = mdi;
    dx[i] = pdi + mdi === 0 ? 0 : (100 * Math.abs(pdi - mdi)) / (pdi + mdi);
  }
  // ADX = Wilder smoothing of DX
  const start = period * 2;
  if (n > start) {
    const seed = dx.slice(period + 1, start + 1).filter((v) => v != null);
    if (seed.length) {
      let adx = seed.reduce((a, b) => a + b, 0) / seed.length;
      out.adx[start] = adx;
      for (let i = start + 1; i < n; i++) { if (dx[i] == null) continue; adx = (adx * (period - 1) + dx[i]) / period; out.adx[i] = adx; }
    }
  }
  return { adx: lineData(c, out.adx), plusDi: lineData(c, out.plusDi), minusDi: lineData(c, out.minusDi) };
};

const calcSupertrend = (c, { period, mult = 3 }) => {
  const atr = atrArr(c, period);
  const up = c.map(() => null), down = c.map(() => null);
  let trend = 1, finalUpper = null, finalLower = null;
  c.forEach((x, i) => {
    if (atr[i] == null) return;
    const hl2 = (x.high + x.low) / 2;
    const basicUpper = hl2 + mult * atr[i];
    const basicLower = hl2 - mult * atr[i];
    const prevClose = c[i - 1]?.close;
    finalUpper = finalUpper == null || basicUpper < finalUpper || prevClose > finalUpper ? basicUpper : finalUpper;
    finalLower = finalLower == null || basicLower > finalLower || prevClose < finalLower ? basicLower : finalLower;
    if (trend === 1 && x.close < finalLower) trend = -1;
    else if (trend === -1 && x.close > finalUpper) trend = 1;
    if (trend === 1) up[i] = finalLower; else down[i] = finalUpper;
  });
  return { upTrend: lineData(c, up), downTrend: lineData(c, down) };
};

const calcIchimoku = (c, { period } = {}) => {
  const hi9 = highest(c, 9), lo9 = lowest(c, 9);
  const hi26 = highest(c, 26), lo26 = lowest(c, 26);
  const hi52 = highest(c, 52), lo52 = lowest(c, 52);
  const tenkan = c.map((_, i) => (hi9[i] == null ? null : (hi9[i] + lo9[i]) / 2));
  const kijun = c.map((_, i) => (hi26[i] == null ? null : (hi26[i] + lo26[i]) / 2));
  const spanA = c.map((_, i) => (tenkan[i] == null || kijun[i] == null ? null : (tenkan[i] + kijun[i]) / 2));
  const spanB = c.map((_, i) => (hi52[i] == null ? null : (hi52[i] + lo52[i]) / 2));
  return { tenkan: lineData(c, tenkan), kijun: lineData(c, kijun), spanA: lineData(c, spanA), spanB: lineData(c, spanB) };
};

const calcStoch = (c, { period, dPeriod = 3 }) => {
  const hh = highest(c, period), ll = lowest(c, period);
  const k = c.map((x, i) => (hh[i] == null || hh[i] === ll[i] ? null : (100 * (x.close - ll[i])) / (hh[i] - ll[i])));
  const d = smaArr(k.map((v) => (v == null ? 0 : v)), dPeriod).map((v, i) => (k[i] == null ? null : v));
  return { k: lineData(c, k), d: lineData(c, d) };
};

const calcPsar = (c, { step = 0.02, max = 0.2 } = {}) => {
  if (c.length < 2) return { line: [] };
  const out = c.map(() => null);
  let bull = c[1].close >= c[0].close;
  let af = step;
  let ep = bull ? c[0].high : c[0].low;
  let sar = bull ? c[0].low : c[0].high;
  for (let i = 1; i < c.length; i++) {
    sar = sar + af * (ep - sar);
    if (bull) {
      if (c[i].low < sar) { bull = false; sar = ep; ep = c[i].low; af = step; }
      else { if (c[i].high > ep) { ep = c[i].high; af = Math.min(max, af + step); } sar = Math.min(sar, c[i - 1].low, c[i].low); }
    } else {
      if (c[i].high > sar) { bull = true; sar = ep; ep = c[i].high; af = step; }
      else { if (c[i].low < ep) { ep = c[i].low; af = Math.min(max, af + step); } sar = Math.max(sar, c[i - 1].high, c[i].high); }
    }
    out[i] = sar;
  }
  return { line: lineData(c, out) };
};

const calcCci = (c, { period }) => {
  const tp = c.map((x) => (x.high + x.low + x.close) / 3);
  const sma = smaArr(tp, period);
  const out = c.map((_, i) => {
    if (sma[i] == null) return null;
    const slice = tp.slice(i - period + 1, i + 1);
    const md = slice.reduce((a, b) => a + Math.abs(b - sma[i]), 0) / period;
    return md === 0 ? 0 : (tp[i] - sma[i]) / (0.015 * md);
  });
  return { line: lineData(c, out) };
};

const calcMomentum = (c, { period }) => {
  const cl = c.map((x) => x.close);
  const out = cl.map((v, i) => (i < period ? null : v - cl[i - period]));
  return { line: lineData(c, out) };
};

const calcWilliams = (c, { period }) => {
  const hh = highest(c, period), ll = lowest(c, period);
  const out = c.map((x, i) => (hh[i] == null || hh[i] === ll[i] ? null : (-100 * (hh[i] - x.close)) / (hh[i] - ll[i])));
  return { line: lineData(c, out) };
};

const calcDonchian = (c, { period }) => {
  const hh = highest(c, period), ll = lowest(c, period);
  const mid = c.map((_, i) => (hh[i] == null ? null : (hh[i] + ll[i]) / 2));
  return { upper: lineData(c, hh), mid: lineData(c, mid), lower: lineData(c, ll) };
};

const calcKeltner = (c, { period, mult = 2 }) => {
  const mid = emaArr(c.map((x) => x.close), period);
  const atr = atrArr(c, period);
  const up = c.map((_, i) => (mid[i] == null || atr[i] == null ? null : mid[i] + mult * atr[i]));
  const lo = c.map((_, i) => (mid[i] == null || atr[i] == null ? null : mid[i] - mult * atr[i]));
  return { upper: lineData(c, up), mid: lineData(c, mid), lower: lineData(c, lo) };
};

const calcVolume = (c) =>
  // No true volume from Deriv on these instruments — render bar range as a
  // transparent activity proxy, coloured by candle direction.
  ({ hist: c.map((x) => ({ time: x.time, value: Math.abs(x.high - x.low), color: x.close >= x.open ? "rgba(34,197,94,0.45)" : "rgba(239,68,68,0.45)" })) });

const calcObv = (c) => {
  // On-Balance Volume. Deriv gives no true volume, so the bar range is used as a
  // transparent volume proxy (consistent with Volume/VWAP).
  let obv = 0;
  const out = c.map((x, i) => {
    if (i === 0) return 0;
    const vol = Math.max(x.high - x.low, 1e-9);
    if (x.close > c[i - 1].close) obv += vol; else if (x.close < c[i - 1].close) obv -= vol;
    return obv;
  });
  return { line: lineData(c, out) };
};

// ── definitions ────────────────────────────────────────────────────────────
// pane:false → overlay on the price chart. pane:true → its own sub-pane.
// useUserColor: the single line uses the user-picked palette colour.
export const IND_DEFS = {
  ema: { label: "EMA", def: 9, pane: false, calc: calcEma, series: [{ key: "line", kind: "line", width: 2, useUserColor: true }] },
  sma: { label: "SMA", def: 14, pane: false, calc: calcSma, series: [{ key: "line", kind: "line", width: 2, useUserColor: true }] },
  vwap: { label: "VWAP", def: 0, pane: false, noPeriod: true, calc: calcVwap, series: [{ key: "line", kind: "line", width: 2, useUserColor: true }] },
  boll: { label: "Bollinger Bands", def: 20, pane: false, calc: calcBoll, series: [
    { key: "upper", kind: "line", width: 1, useUserColor: true },
    { key: "mid", kind: "line", width: 1, style: 2, useUserColor: true },
    { key: "lower", kind: "line", width: 1, useUserColor: true },
  ] },
  donchian: { label: "Donchian Channel", def: 20, pane: false, calc: calcDonchian, series: [
    { key: "upper", kind: "line", width: 1, color: "#38bdf8" },
    { key: "mid", kind: "line", width: 1, style: 2, color: "#8b93a7" },
    { key: "lower", kind: "line", width: 1, color: "#38bdf8" },
  ] },
  keltner: { label: "Keltner Channel", def: 20, pane: false, calc: calcKeltner, series: [
    { key: "upper", kind: "line", width: 1, color: "#c084fc" },
    { key: "mid", kind: "line", width: 1, style: 2, color: "#c084fc" },
    { key: "lower", kind: "line", width: 1, color: "#c084fc" },
  ] },
  supertrend: { label: "SuperTrend", def: 10, pane: false, calc: calcSupertrend, series: [
    { key: "upTrend", kind: "line", width: 2, color: "#22c55e" },
    { key: "downTrend", kind: "line", width: 2, color: "#ef4444" },
  ] },
  ichimoku: { label: "Ichimoku", def: 0, pane: false, noPeriod: true, calc: calcIchimoku, series: [
    { key: "tenkan", kind: "line", width: 1, color: "#38bdf8" },
    { key: "kijun", kind: "line", width: 1, color: "#f43f5e" },
    { key: "spanA", kind: "line", width: 1, color: "#22c55e" },
    { key: "spanB", kind: "line", width: 1, color: "#f59e0b" },
  ] },
  psar: { label: "Parabolic SAR", def: 0, pane: false, noPeriod: true, calc: calcPsar, series: [
    { key: "line", kind: "line", width: 1, style: 1, useUserColor: true },
  ] },
  rsi: { label: "RSI", def: 14, pane: true, calc: calcRsi, series: [{ key: "line", kind: "line", width: 1, useUserColor: true }] },
  macd: { label: "MACD", def: 0, pane: true, noPeriod: true, calc: calcMacd, series: [
    { key: "hist", kind: "hist" },
    { key: "macd", kind: "line", width: 1, color: "#38bdf8" },
    { key: "signal", kind: "line", width: 1, color: "#f59e0b" },
  ] },
  atr: { label: "ATR", def: 14, pane: true, calc: calcAtr, series: [{ key: "line", kind: "line", width: 1, useUserColor: true }] },
  adx: { label: "ADX", def: 14, pane: true, calc: calcAdx, series: [
    { key: "adx", kind: "line", width: 2, color: "#e5e7eb" },
    { key: "plusDi", kind: "line", width: 1, color: "#22c55e" },
    { key: "minusDi", kind: "line", width: 1, color: "#ef4444" },
  ] },
  stoch: { label: "Stochastic", def: 14, pane: true, calc: calcStoch, series: [
    { key: "k", kind: "line", width: 1, color: "#38bdf8" },
    { key: "d", kind: "line", width: 1, color: "#f59e0b" },
  ] },
  cci: { label: "CCI", def: 20, pane: true, calc: calcCci, series: [{ key: "line", kind: "line", width: 1, useUserColor: true }] },
  momentum: { label: "Momentum", def: 10, pane: true, calc: calcMomentum, series: [{ key: "line", kind: "line", width: 1, useUserColor: true }] },
  williams: { label: "Williams %R", def: 14, pane: true, calc: calcWilliams, series: [{ key: "line", kind: "line", width: 1, useUserColor: true }] },
  volume: { label: "Volume", def: 0, pane: true, noPeriod: true, calc: calcVolume, series: [{ key: "hist", kind: "hist" }] },
  obv: { label: "OBV", def: 0, pane: true, noPeriod: true, calc: calcObv, series: [{ key: "line", kind: "line", width: 1, useUserColor: true }] },
};

export const IND_ORDER = [
  "ema", "sma", "vwap", "boll", "donchian", "keltner", "supertrend", "ichimoku", "psar",
  "rsi", "macd", "stoch", "atr", "adx", "cci", "momentum", "williams", "volume", "obv",
];

// Grouped for the indicator picker UI.
export const IND_CATEGORIES = [
  { name: "Trend", keys: ["ema", "sma", "vwap", "supertrend", "ichimoku", "psar"] },
  { name: "Momentum", keys: ["rsi", "macd", "stoch", "cci", "momentum", "williams", "adx"] },
  { name: "Volatility", keys: ["boll", "atr", "keltner", "donchian"] },
  { name: "Volume", keys: ["obv", "volume"] },
];

export const isPaneInd = (t) => !!IND_DEFS[t]?.pane;
export const indLabel = (t) => IND_DEFS[t]?.label || t;
export const indHasPeriod = (t) => !IND_DEFS[t]?.noPeriod;
export const indDefault = (t) => IND_DEFS[t]?.def ?? 14;

// Compute all series arrays for an active indicator instance.
export const computeInd = (it, candles) => {
  const def = IND_DEFS[it.type];
  if (!def) return {};
  try { return def.calc(candles, { period: it.period, mult: it.mult }); }
  catch { return {}; }
};
